import React, { useState, useEffect } from "react";
import {
  Form,
  Radio,
  Slider,
  Space,
  Card,
  Typography,
  Spin,
  Progress,
  Row,
  Col,
  Badge,
  DatePicker,
} from "antd";
import StepHeader from "../../../Evaluation/StepHeader";
import { usePermissions } from "../../../../PermissionsProvider";
import { IntentService } from "../../../../services/IntentService";
import { ApplicationService } from "../../../../services/ApplicationService";
import { DatasetData } from "../../../../model_gen/dataset";
import {
  DatabaseOutlined,
  PercentageOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { DatasetService } from "../../../../services/DatasetService";
import dayjs from "dayjs";
import type { RangePickerProps } from "antd/es/date-picker";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface DatasetSamplingProps {
  datasetData: DatasetData;
  ignoredApplicationIds: number[];
  onChange: (data: Partial<DatasetData>) => void;
}

const DatasetSampling: React.FC<DatasetSamplingProps> = ({
  datasetData,
  ignoredApplicationIds,
  onChange,
}) => {
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [baseLabel, setBaseLabel] = useState<string>("");

  // Fetch data when needed parameters are available
  const fetchRequestCount = () => {
    if (
      !apiKey ||
      !(
        (datasetData.base_type === "intent_group" && datasetData.intent_group_id) ||
        (datasetData.base_type === "application_intent" && datasetData.application_id && datasetData.intent_id)
      )
    ) {
      return;
    }

    setLoading(true);

    // Convert intentGroupId, applicationId, and intentId to the correct format for the API
    const igId = datasetData.base_type === "intent_group" ? datasetData.intent_group_id : null;
    const appId = datasetData.base_type === "application_intent" ? datasetData.application_id : null;
    const intId = datasetData.base_type === "application_intent" ? datasetData.intent_id : null;

    DatasetService.getEstimatedDatasetRequestCount(
      igId,
      appId,
      intId,
      datasetData.date_start,
      datasetData.date_end,
      ignoredApplicationIds,
      setLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        console.log("Estimated request count response:", response);
        let total = 0;
        if (response && typeof response.count === "number") {
          total = response.count;
        }
        setTotalRequests(total);
        setLoading(false);
        onChange({ total_requests: total });
      },
      (error: any) => {
        console.error("Error fetching estimated request count:", error);
        setLoading(false);
      }
    );
  };

  // Fetch data when component mounts or when key parameters change
  useEffect(() => {
    fetchRequestCount();
  }, [datasetData.base_type, datasetData.intent_group_id, datasetData.application_id, datasetData.intent_id, datasetData.date_start, datasetData.date_end, ignoredApplicationIds, apiKey]);

  // Get intent group or application/intent names for display
  useEffect(() => {
    if (!apiKey) return;

    if (datasetData.base_type === "intent_group" && datasetData.intent_group_id) {
      IntentService.getIntentGroup(
        datasetData.intent_group_id,
        false,
        setLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Intent group response:", response);
          let name = `Intent Group ${datasetData.intent_group_id}`;
          if (response) {
            name = response.intent_name || response.name || name;
          }
          setBaseLabel(`Intent Group: ${name}`);
        },
        (error: any) => {
          console.error("Error fetching intent group:", error);
          setBaseLabel(`Intent Group: ${datasetData.intent_group_id}`);
        }
      );
    } else if (datasetData.base_type === "application_intent" && datasetData.application_id && datasetData.intent_id) {
      ApplicationService.getApplication(
        datasetData.application_id,
        setLoading,
        apiKey
      ).subscribe(
        (appResponse: any) => {
          console.log("Application response:", appResponse);
          const appName =
            appResponse?.application_name ||
            appResponse?.name ||
            `Application ${datasetData.application_id}`;

          ApplicationService.getApplicationAction(
            datasetData.application_id,
            datasetData.intent_id,
            setLoading,
            apiKey
          ).subscribe(
            (intentResponse: any) => {
              console.log("Intent response:", intentResponse);
              const intentName =
                intentResponse?.action_type || `Intent ${datasetData.intent_id}`;
              setBaseLabel(`Application: ${appName}, Intent: ${intentName}`);
            },
            (error: any) => {
              console.error("Error fetching intent:", error);
              setBaseLabel(`Application: ${appName}, Intent: ${datasetData.intent_id}`);
            }
          );
        },
        (error: any) => {
          console.error("Error fetching application:", error);
          setBaseLabel(`Application: ${datasetData.application_id}, Intent: ${datasetData.intent_id}`);
        }
      );
    }
  }, [datasetData.base_type, datasetData.intent_group_id, datasetData.application_id, datasetData.intent_id, apiKey]);

  const calculateSampleSize = () => {
    if (datasetData.sample_type === "all") {
      return totalRequests;
    } else if (datasetData.sample_type === "random" && datasetData.sample_percentage) {
      return Math.round((totalRequests * datasetData.sample_percentage) / 100);
    } else {
      return 0;
    }
  };

  const handleDateRangeChange: RangePickerProps["onChange"] = (dates) => {
    const startDate = dates && dates[0] ? dates[0].startOf("day").valueOf() : undefined;
    const endDate = dates && dates[1] ? dates[1].endOf("day").valueOf() : undefined;
    
    onChange({ 
      date_start: startDate,
      date_end: endDate
    });
    
    // Refresh request count to get updated total for the selected date range
    fetchRequestCount();
  };

  const sampleSize = calculateSampleSize();
  const samplePercentageOfTotal =
    totalRequests > 0 ? (sampleSize / totalRequests) * 100 : 0;

  // Should we show the date range picker (only for all or random)

  return (
    <Space
      direction="vertical"
      size="large"
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
      }}>
      <StepHeader
        title="Choose Sampling Method"
        description="Select how you want to sample data for your dataset"
        style={{ textAlign: "center" }}
      />

      <div style={{ width: "600px" }}>
        {/* Selected Base Info Card */}
        <Card style={{ marginBottom: "24px" }} loading={loading && !baseLabel}>
          <Row align="middle" gutter={16}>
            <Col>
              <DatabaseOutlined
                style={{ fontSize: "24px", color: "#1890ff" }}
              />
            </Col>
            <Col flex="auto">
              <Title level={5} style={{ margin: 0 }}>
                Selected Base
              </Title>
              <Text>{baseLabel || "Loading..."}</Text>
            </Col>
            <Col>
              <Badge
                count={totalRequests}
                overflowCount={9999}
                style={{
                  backgroundColor: "#52c41a",
                }}
                title={`${totalRequests} requests available`}
              />
            </Col>
          </Row>
        </Card>

        {/* Sampling Method Selection */}
        <Card title="Sampling Method" style={{ marginBottom: "24px" }}>
          <Form layout="vertical">
            <Form.Item
              required
              tooltip="Choose how to sample data for your dataset">
              <Radio.Group
                value={datasetData.sample_type}
                onChange={(e) => onChange({ sample_type: e.target.value })}
                style={{ width: "100%" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Radio value="all">
                    <Space>
                      <Text strong>Use All Requests</Text>
                      <Text type="secondary">
                        Include all available requests in the dataset
                      </Text>
                    </Space>
                  </Radio>
                  <Radio value="random">
                    <Space>
                      <Text strong>Random Sample</Text>
                      <Text type="secondary">
                        Select a random percentage of requests
                      </Text>
                    </Space>
                  </Radio>
                  <Radio value="none">
                    <Space>
                      <Text strong>None (Empty Dataset)</Text>
                      <Text type="secondary">
                        Create an empty dataset with no requests
                      </Text>
                    </Space>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {datasetData.sample_type === "random" && (
              <>
                {/* Date Range Filter */}
                <Form.Item
                  label="Filter by Date Range (Optional)"
                  tooltip="Optionally filter requests by date range">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <RangePicker
                      style={{ width: "100%" }}
                      value={[
                        datasetData.date_start ? dayjs(datasetData.date_start) : null,
                        datasetData.date_end ? dayjs(datasetData.date_end) : null
                      ]}
                      onChange={handleDateRangeChange}
                      allowEmpty={[true, true]}
                      placeholder={["Start Date", "End Date"]}
                    />
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    <Text type="secondary">
                      Leave both fields empty to include all dates
                    </Text>
                  </div>
                </Form.Item>

                {/* Sample Percentage */}
                <Form.Item
                  label={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}>
                      <span>Sample Percentage</span>
                      <span
                        style={{
                          fontWeight: "bold",
                          color: "#21b892",
                          marginLeft: "20px",
                        }}>
                        {datasetData.sample_percentage}%
                      </span>
                    </div>
                  }
                  required
                  tooltip="Percentage of requests to include in the dataset">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <PercentageOutlined
                      style={{ marginRight: "10px", color: "#21b892" }}
                    />
                    <Slider
                      min={1}
                      max={100}
                      value={datasetData.sample_percentage}
                      onChange={(value: number) =>
                        onChange({ sample_percentage: value })
                      }
                      style={{ flex: 1 }}
                      tooltipVisible={false}
                      trackStyle={{ backgroundColor: "#21b892" }}
                      handleStyle={{
                        borderColor: "#21b892",
                        backgroundColor: "#21b892",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      marginTop: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}>
                    <Text type="secondary">1%</Text>
                    <Text type="secondary">50%</Text>
                    <Text type="secondary">100%</Text>
                  </div>
                </Form.Item>
              </>
            )}

            {/* Date Range Filter - Only show for all sample type */}
            {datasetData.sample_type === "all" && (
              <Form.Item
                label="Filter by Date Range (Optional)"
                tooltip="Optionally filter requests by date range">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={[
                      datasetData.date_start ? dayjs(datasetData.date_start) : null,
                      datasetData.date_end ? dayjs(datasetData.date_end) : null
                    ]}
                    onChange={handleDateRangeChange}
                    allowEmpty={[true, true]}
                    placeholder={["Start Date", "End Date"]}
                  />
                </div>
                <div style={{ marginTop: "8px" }}>
                  <Text type="secondary">
                    Leave both fields empty to include all dates
                  </Text>
                </div>
              </Form.Item>
            )}
          </Form>
        </Card>

        {/* Dataset Size Preview */}
        <Card title="Dataset Size Preview">
          <Row gutter={[16, 16]} align="middle">
            <Col span={24}>
              <Progress
                percent={samplePercentageOfTotal}
                status="active"
                format={(percent) => `${Math.round(percent!)}%`}
                strokeColor={{
                  "0%": "#21b892",
                  "100%": "#FFDB6A",
                }}
              />
            </Col>
            <Col span={12}>
              <Card bordered={false} style={{ background: "#f5f5f5" }}>
                <Statistic
                  title="Total Available"
                  value={totalRequests}
                  suffix="requests"
                  loading={loading}
                  valueStyle={{ color: "#21b892" }}
                  prefix={<DatabaseOutlined />}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card bordered={false} style={{ background: "#f5f5f5" }}>
                <Statistic
                  title="Estimated Dataset Size"
                  value={sampleSize}
                  suffix="requests"
                  valueStyle={{ color: "#FFDB6A" }}
                  prefix={<InboxOutlined />}
                />
              </Card>
            </Col>
          </Row>
        </Card>
      </div>
    </Space>
  );
};

// Helper Statistic component since we can't import it directly
const Statistic = ({
  title,
  value,
  suffix,
  loading,
  valueStyle,
  prefix,
}: any) => {
  return (
    <div>
      <div
        style={{
          marginBottom: "8px",
          color: "rgba(0, 0, 0, 0.45)",
          fontSize: "14px",
        }}>
        {title}
      </div>
      {loading ? (
        <Spin size="small" />
      ) : (
        <div style={{ ...valueStyle, fontSize: "24px", fontWeight: 500 }}>
          {prefix && <span style={{ marginRight: "8px" }}>{prefix}</span>}
          {value}{" "}
          {suffix && (
            <span style={{ fontSize: "16px", color: "rgba(0, 0, 0, 0.65)" }}>
              {suffix}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default DatasetSampling;
